import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../context/NotificationContext';
import './UserManagement.css'; // Assuming it shares styles
import HotelFormModal from './HotelFormModal';
import ConfirmModal from './ConfirmModal';

function HotelManagement() {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingHotel, setEditingHotel] = useState(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [hotelToDelete, setHotelToDelete] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [paginationData, setPaginationData] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 });
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const { showNotification } = useNotification();

    const getAuthHeader = useCallback(() => {
        const token = localStorage.getItem('token');
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    }, []);

    const fetchHotels = useCallback(async (page = 1, search = '', currentSortConfig) => {
        setLoading(true);
        setError(null);
        
        const limit = 10;
        const apiUrl = new URL(`${process.env.REACT_APP_API_URL}/api.php`);
        apiUrl.searchParams.append('path', 'hotels');
        apiUrl.searchParams.append('page', page);
        apiUrl.searchParams.append('limit', limit);
        if (search) {
            apiUrl.searchParams.append('search', search);
        }
        apiUrl.searchParams.append('sortBy', currentSortConfig.key);
        apiUrl.searchParams.append('direction', currentSortConfig.direction);

        try {
            const response = await fetch(apiUrl, { headers: getAuthHeader() });
            if (!response.ok) { throw new Error(`Error del servidor: ${response.status}`); }
            
            const data = await response.json();
            if (data.success && data.data) {
                setHotels(data.data.hotels || []);
                setPaginationData(data.data.pagination || {});
            } else { 
                throw new Error(data.error?.message || 'Error al cargar los hoteles.'); 
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    useEffect(() => {
        const timerId = setTimeout(() => {
            fetchHotels(1, searchTerm, sortConfig);
        }, 500);
        return () => clearTimeout(timerId);
    }, [searchTerm, sortConfig, fetchHotels]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= paginationData.totalPages) {
            fetchHotels(newPage, searchTerm, sortConfig);
        }
    };
    
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    };

    const handleAddClick = () => { setEditingHotel(null); setIsFormModalOpen(true); };
    const handleEditClick = (hotel) => { setEditingHotel(hotel); setIsFormModalOpen(true); };
    const handleDeleteRequest = (hotel) => { setHotelToDelete(hotel); setIsConfirmModalOpen(true); };
    const handleHotelSaved = () => { fetchHotels(paginationData.currentPage, searchTerm, sortConfig); };
    
    const handleConfirmDelete = async () => {
        if (!hotelToDelete) return;
        try {
            const apiUrl = `${process.env.REACT_APP_API_URL}/api.php?path=hotels/${hotelToDelete.hotel_id}`;
            const response = await fetch(apiUrl, {
                method: 'DELETE',
                headers: getAuthHeader()
            });
            const result = await response.json();
            if (response.ok && result.success) {
                showNotification(result.data.message || 'Hotel eliminado!', 'success');
                fetchHotels(1, '', sortConfig);
            } else {
                throw new Error(result.error?.message || 'Error desconocido');
            }
        } catch (err) {
            showNotification(`Error al eliminar: ${err.message}`, 'error');
        } finally {
            setIsConfirmModalOpen(false);
            setHotelToDelete(null);
        }
    };

    return (
        <>
            <div className="user-management-container">
                <header className="main-header">
                    <h1 className="page-title">Administración de Hoteles</h1>
                </header>
                <section className="info-entry-section">
                    <div className="user-list-header">
                        <h2 className="report-subtitle">Hoteles del Sistema</h2>
                        <button className="add-user-btn" onClick={handleAddClick}>+ Agregar Hotel</button>
                    </div>
                    <div className="table-controls">
                        <input 
                            type="text"
                            placeholder="Buscar por nombre o ciudad..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {loading && <p>Cargando...</p>}
                    {error && <p className="error-message">{error}</p>}
                    {!loading && !error && (
                        <div className="data-table-container">
                            <table className="data-input-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => requestSort('name')}>Nombre{getSortIndicator('name')}</th>
                                        <th onClick={() => requestSort('city')}>Ciudad{getSortIndicator('city')}</th>
                                        <th onClick={() => requestSort('type')}>Tipo{getSortIndicator('type')}</th>
                                        <th onClick={() => requestSort('total_rooms_available')}>Cuartos{getSortIndicator('total_rooms_available')}</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {hotels.map(hotel => (
                                        <tr key={hotel.hotel_id}>
                                            <td>{hotel.name}</td>
                                            <td>{hotel.city || 'N/A'}</td>
                                            <td>{hotel.type || 'N/A'}</td>
                                            <td>{hotel.total_rooms_available}</td>
                                            <td className="action-buttons">
                                                <button className="edit-btn" onClick={() => handleEditClick(hotel)}>Editar</button>
                                                <button className="delete-btn" onClick={() => handleDeleteRequest(hotel)}>Eliminar</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {!loading && paginationData.totalItems > 0 && (
                        <div className="table-controls">
                             <span className="pagination-info">
                                Mostrando {((paginationData.currentPage - 1) * paginationData.itemsPerPage) + 1} - {Math.min(paginationData.currentPage * paginationData.itemsPerPage, paginationData.totalItems)} de {paginationData.totalItems}
                            </span>
                            <div className="pagination-controls">
                                <button onClick={() => handlePageChange(1)} disabled={paginationData.currentPage === 1}>&lt;&lt;</button>
                                <button onClick={() => handlePageChange(paginationData.currentPage - 1)} disabled={paginationData.currentPage === 1}>&lt;</button>
                                <span className="pagination-info">Página {paginationData.currentPage} de {paginationData.totalPages}</span>
                                <button onClick={() => handlePageChange(paginationData.currentPage + 1)} disabled={paginationData.currentPage === paginationData.totalPages}>&gt;</button>
                                <button onClick={() => handlePageChange(paginationData.totalPages)} disabled={paginationData.currentPage === paginationData.totalPages}>&gt;&gt;</button>
                            </div>
                        </div>
                    )}
                </section>
            </div>
            <HotelFormModal 
                isOpen={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                onHotelSaved={handleHotelSaved}
                editingHotel={editingHotel}
            />
            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Confirmar Eliminación de Hotel"
            >
                ¿Está seguro de que desea eliminar el hotel "{hotelToDelete?.name}"?
            </ConfirmModal>
        </>
    );
}

export default HotelManagement;